/**
 * Phase 2b — Unified worksheet action/filter resolver.
 *
 * Collapses the six-map `ActionResult` shape (global filters, worksheet-scoped
 * filters, explicit empties, operator maps, kind maps, tuple filters,
 * highlights, parameters) into a single normalized representation for one
 * target worksheet.
 *
 * This is the ONLY place in production code that should interpret an
 * `ActionResult`. Every consumer (extract-backed execution, dashboard tiles,
 * oracle transport, worker interception, and the temporary post-filter shim)
 * reads from the `ResolvedWorksheetActionState` emitted here and never re-
 * derives filter semantics. Preserving this invariant is the Phase 2b exit
 * gate; it also seats every downstream Rust/TS translator on one source of
 * truth for Phase 6.
 */

import type { ActionResult } from '../tableau/actionRuntime'

export type EffectiveFilterOperator = 'include' | 'exclude'

export type EffectiveFilterKind = 'dimension' | 'measure' | 'range' | 'tuple'

export type EffectiveFilter =
  | {
      kind: 'dimension' | 'measure' | 'range'
      fieldName: string
      operator: EffectiveFilterOperator
      values: readonly any[]
      /** True for the explicit-empty worksheet filter case: "exclude all values". */
      explicitEmpty: boolean
      isMeasure: boolean
    }
  | {
      kind: 'tuple'
      operator: EffectiveFilterOperator
      tuples: ReadonlyArray<Record<string, any>>
      explicitEmpty: false
      isMeasure: false
      /** Tuple filters carry no single fieldName. */
      fieldName: null
    }

export type ResolvedWorksheetActionState = {
  /** Worksheet name this resolution is scoped to, or null for global scope. */
  worksheetName: string | null
  filters: readonly EffectiveFilter[]
  highlights: Readonly<Record<string, readonly any[]>>
  parameters: Readonly<Record<string, any>>
  /** Diagnostics only; not a behavior signal. */
  source: {
    globalFilterCount: number
    worksheetScopedFilterCount: number
    explicitEmptyFilterCount: number
    tupleFilterCount: number
  }
}

const normalizeText = (value: any): string => String(value ?? '').trim()

const normalizeWorksheetKey = (value: any): string => normalizeText(value).toLowerCase()

const unwrapFilterValue = (value: any): any => {
  if (value === null || typeof value === 'undefined') return value
  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value)) return value.map(unwrapFilterValue)
  if (typeof value !== 'object') {
    if (typeof value === 'string' && value.trim() === '[object Object]') return null
    return value
  }
  const obj = value as Record<string, any>
  if ('value' in obj) return unwrapFilterValue(obj.value)
  if ('v' in obj) return unwrapFilterValue(obj.v)
  if ('raw' in obj) return unwrapFilterValue(obj.raw)
  if ('key' in obj) return unwrapFilterValue(obj.key)
  for (const candidate of ['id', 'name', 'caption', 'label', 'text'] as const) {
    const candidateValue = obj[candidate]
    if (typeof candidateValue === 'string' || typeof candidateValue === 'number') {
      return candidateValue
    }
  }
  const entries = Object.entries(obj)
  if (entries.length === 1) {
    const only = entries[0][1]
    if (typeof only === 'string' || typeof only === 'number' || typeof only === 'boolean') {
      return only
    }
  }
  return null
}

const pickWorksheetScopedMap = <V>(
  byWorksheet: Record<string, Record<string, V>> | null | undefined,
  worksheetName: string | null,
): Record<string, V> => {
  if (!byWorksheet || typeof byWorksheet !== 'object' || !worksheetName) return {}
  const want = normalizeWorksheetKey(worksheetName)
  for (const [k, v] of Object.entries(byWorksheet)) {
    if (normalizeWorksheetKey(k) === want && v && typeof v === 'object') return v
  }
  return {}
}

const pickWorksheetTupleFilter = (
  tupleFiltersByWorksheet: Record<string, any> | null | undefined,
  worksheetName: string | null,
): any => {
  if (!tupleFiltersByWorksheet || typeof tupleFiltersByWorksheet !== 'object' || !worksheetName) {
    return null
  }
  const want = normalizeWorksheetKey(worksheetName)
  for (const [k, v] of Object.entries(tupleFiltersByWorksheet)) {
    if (normalizeWorksheetKey(k) === want) return v
  }
  return null
}

const mergeFieldNameSet = (...maps: Array<Record<string, any> | null | undefined>): string[] => {
  const out = new Set<string>()
  for (const m of maps) {
    if (!m || typeof m !== 'object') continue
    for (const key of Object.keys(m)) {
      const normalized = normalizeText(key)
      if (normalized) out.add(normalized)
    }
  }
  return Array.from(out).sort()
}

const mergeScalarMap = <V>(
  primary: Record<string, V> | null | undefined,
  override: Record<string, V> | null | undefined,
): Record<string, V> => {
  const out: Record<string, V> = {}
  if (primary && typeof primary === 'object') {
    for (const [k, v] of Object.entries(primary)) {
      const key = normalizeText(k)
      if (key) out[key] = v
    }
  }
  if (override && typeof override === 'object') {
    for (const [k, v] of Object.entries(override)) {
      const key = normalizeText(k)
      if (key) out[key] = v
    }
  }
  return out
}

const mergeValueArrayMap = (
  global: Record<string, any[]> | null | undefined,
  worksheetScoped: Record<string, any[]> | null | undefined,
): Record<string, any[]> => {
  const out: Record<string, any[]> = {}
  if (global && typeof global === 'object') {
    for (const [k, v] of Object.entries(global)) {
      const key = normalizeText(k)
      if (!key) continue
      out[key] = Array.isArray(v) ? v.map(unwrapFilterValue) : []
    }
  }
  if (worksheetScoped && typeof worksheetScoped === 'object') {
    for (const [k, v] of Object.entries(worksheetScoped)) {
      const key = normalizeText(k)
      if (!key) continue
      out[key] = Array.isArray(v) ? v.map(unwrapFilterValue) : []
    }
  }
  return out
}

const deriveKind = (
  kindMap: Record<string, 'select' | 'range'> | undefined,
  fieldName: string,
  isMeasureHint: boolean,
): 'dimension' | 'measure' | 'range' => {
  const raw = kindMap?.[fieldName]
  if (raw === 'range') return 'range'
  if (isMeasureHint) return 'measure'
  return 'dimension'
}

export function resolveWorksheetActionState(
  actionState: ActionResult | null | undefined,
  worksheetName: string | null | undefined,
  opts: {
    /** Fields known to be measures. If omitted, the resolver treats everything as dimension unless kindMap overrides. */
    measureFieldNames?: readonly string[]
  } = {},
): ResolvedWorksheetActionState {
  const name = normalizeText(worksheetName) || null
  if (!actionState) {
    return {
      worksheetName: name,
      filters: [],
      highlights: {},
      parameters: {},
      source: {
        globalFilterCount: 0,
        worksheetScopedFilterCount: 0,
        explicitEmptyFilterCount: 0,
        tupleFilterCount: 0,
      },
    }
  }

  const measureSet = new Set(
    (opts.measureFieldNames ?? []).map((f) => normalizeText(f)).filter(Boolean),
  )

  const worksheetFilters = pickWorksheetScopedMap(actionState.filtersByWorksheet, name)
  const worksheetExplicitEmpty = pickWorksheetScopedMap(
    actionState.explicitEmptyFiltersByWorksheet,
    name,
  )
  const worksheetOps = pickWorksheetScopedMap(actionState.filterOpsByWorksheet, name)
  const worksheetKinds = pickWorksheetScopedMap(actionState.filterKindsByWorksheet, name)
  const worksheetHighlights = pickWorksheetScopedMap(actionState.highlightsByWorksheet, name)
  const worksheetParameters = pickWorksheetScopedMap(
    (actionState as any).parametersByWorksheet ?? null,
    name,
  )
  const worksheetTupleFilter = pickWorksheetTupleFilter(
    (actionState as any).tupleFiltersByWorksheet ?? null,
    name,
  )

  const mergedFilters = mergeValueArrayMap(actionState.filters ?? {}, worksheetFilters)
  const mergedExplicitEmpty: Record<string, true> = {
    ...(actionState.explicitEmptyFilters ?? {}),
    ...worksheetExplicitEmpty,
  }
  const mergedOps = mergeScalarMap(actionState.filterOps, worksheetOps)
  const mergedKinds = mergeScalarMap(actionState.filterKinds, worksheetKinds)
  const mergedHighlights = mergeValueArrayMap(actionState.highlights ?? {}, worksheetHighlights)
  const mergedParameters = mergeScalarMap(actionState.parameters ?? {}, worksheetParameters)

  const allFilterFieldNames = mergeFieldNameSet(mergedFilters, mergedExplicitEmpty)

  const filters: EffectiveFilter[] = []
  for (const fieldName of allFilterFieldNames) {
    const rawValues = mergedFilters[fieldName] ?? []
    const explicitEmpty = Boolean(mergedExplicitEmpty[fieldName])
    const operator: EffectiveFilterOperator = mergedOps[fieldName] === 'exclude' ? 'exclude' : 'include'
    const isMeasure = measureSet.has(fieldName)
    const kind = deriveKind(mergedKinds, fieldName, isMeasure)
    const values = Array.isArray(rawValues) ? rawValues : []
    // Drop filters that are entirely empty and NOT explicit-empty: those mean "no
    // filter applied" rather than "exclude everything".
    if (!explicitEmpty && values.length === 0) continue
    filters.push({
      kind,
      fieldName,
      operator,
      values,
      explicitEmpty,
      isMeasure,
    })
  }

  const tupleSource =
    worksheetTupleFilter || (actionState as any).tupleFilter || null
  if (tupleSource && typeof tupleSource === 'object') {
    const tuples = Array.isArray((tupleSource as any).tuples)
      ? ((tupleSource as any).tuples as any[]).filter(
          (tuple) => tuple && typeof tuple === 'object',
        )
      : []
    if (tuples.length > 0) {
      filters.push({
        kind: 'tuple',
        fieldName: null,
        operator: (tupleSource as any).op === 'exclude' ? 'exclude' : 'include',
        values: [],
        explicitEmpty: false,
        isMeasure: false,
        tuples,
      } as unknown as EffectiveFilter)
    }
  }

  return {
    worksheetName: name,
    filters,
    highlights: mergedHighlights,
    parameters: mergedParameters,
    source: {
      globalFilterCount: Object.keys(actionState.filters ?? {}).length,
      worksheetScopedFilterCount: Object.keys(worksheetFilters).length,
      explicitEmptyFilterCount: Object.keys(mergedExplicitEmpty).length,
      tupleFilterCount:
        tupleSource && typeof tupleSource === 'object' && Array.isArray((tupleSource as any).tuples)
          ? (tupleSource as any).tuples.length
          : 0,
    },
  }
}
